import { useState } from "react";
import type { ReactNode } from "react";
import { Badge, Card, CardSection, Field, Input, Select } from "@/components/ui";
import type { CameraType, PrinterType } from "@/lib/types/config";
import { useBuilder } from "../BuilderContext";
import { IconCamera, IconMonitor, IconPrinter } from "../components/icons";
import { useAsync } from "@/lib/hooks/useAsync";

const CAMERA_TYPES: Array<{ value: CameraType; label: string }> = [
  { value: "webcam", label: "Webcam (built-in)" },
  { value: "dslr", label: "DSLR / mirrorless" },
  { value: "ipad", label: "iPad / tablet camera" },
  { value: "none", label: "No camera (upload only)" },
];

const PRINTER_TYPES: Array<{ value: PrinterType; label: string }> = [
  { value: "none", label: "None — print from the kiosk browser" },
  { value: "dnp", label: "DNP dye-sublimation" },
  { value: "mitsubishi", label: "Mitsubishi" },
  { value: "canon_selphy", label: "Canon SELPHY" },
  { value: "epson", label: "Epson" },
  { value: "system", label: "System default (CUPS)" },
];

/**
 * Kiosk hardware.
 *
 * `printer_type` decides which server-side print adapter runs: anything other
 * than "none" routes through CUPS on the kiosk host, which needs a queue name.
 */
export function HardwareTab() {
  const { draft, update } = useBuilder();
  const hardware = draft.hardware ?? {};

  const setHardware = (patch: Partial<typeof hardware>) =>
    update((current) => ({ ...current, hardware: { ...current.hardware, ...patch } }));

  const usesCups = Boolean(hardware.printer_type && hardware.printer_type !== "none");
  const devices = useDetectedCameras();

  return (
    <CardSection
      eyebrow="Hardware"
      title="Device configuration"
      description="Choose which cameras and printers the booth should use, and set the screen resolution for the kiosk display."
    >
      <div className="flex flex-col gap-5">
        <Card className="flex flex-col gap-5 p-5">
          <SectionHeading icon={<IconCamera size={15} />} title="Camera" />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Camera type">
              <Select
                value={hardware.camera_type ?? "webcam"}
                onChange={(e) => setHardware({ camera_type: e.target.value as CameraType })}
              >
                {CAMERA_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </Field>

            <Field label="Preferred camera device" hint="Auto-detect works for most single-camera kiosks.">
              <Select
                value={hardware.camera_device ?? ""}
                onChange={(e) => setHardware({ camera_device: e.target.value || undefined })}
              >
                <option value="">Auto-detect</option>
                {devices.data?.map((device) => (
                  <option key={device.deviceId} value={device.deviceId}>
                    {device.label || `Camera ${device.deviceId.slice(0, 6)}`}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>

        <Card className="flex flex-col gap-5 p-5">
          <SectionHeading icon={<IconMonitor size={15} />} title="Display & printer" />
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Screen resolution" hint="Used for layout hints only; the booth is responsive.">
              <Input
                placeholder="1920x1080"
                className="font-mono"
                value={hardware.screen_resolution ?? ""}
                onChange={(e) => setHardware({ screen_resolution: e.target.value || undefined })}
              />
            </Field>

            <Field label="Printer type">
              <Select
                value={hardware.printer_type ?? "none"}
                onChange={(e) => setHardware({ printer_type: e.target.value as PrinterType })}
              >
                {PRINTER_TYPES.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </Select>
            </Field>

            {usesCups && (
              <Field
                label="CUPS queue name"
                hint="Must match `lpstat -p` on the kiosk host. Without it, print jobs fail with “no printer configured”."
                className="sm:col-span-2"
              >
                <Input
                  placeholder="DNP-DS620"
                  className="font-mono"
                  value={hardware.printer_name ?? ""}
                  onChange={(e) => setHardware({ printer_name: e.target.value || undefined })}
                />
              </Field>
            )}
          </div>

          <p className="rounded-lg bg-ink-800 px-3 py-2 text-xs leading-relaxed text-haze-400">
            {usesCups
              ? "The server composes a print-ready image and sends it to this CUPS queue."
              : "With no server-side printer, the booth composes the image and opens the browser's own print dialog on the kiosk."}
          </p>
        </Card>

        <Card className="flex flex-col gap-4 p-5">
          <SectionHeading icon={<IconPrinter size={15} />} title="Detected hardware" />
          <dl className="flex flex-col gap-3 text-sm">
            <div className="flex flex-col gap-1.5">
              <dt className="label-eyebrow">Cameras</dt>
              <dd className="flex flex-wrap items-center gap-2 text-haze-300">
                {devices.loading && <span className="text-xs text-haze-400">Checking…</span>}
                {devices.error && (
                  <span className="text-xs text-haze-400">
                    Permission needed — open the booth once to let the browser list cameras.
                  </span>
                )}
                {devices.data?.length
                  ? devices.data.map((device) => (
                      <Badge key={device.deviceId} tone="neutral">{device.label || "Unnamed camera"}</Badge>
                    ))
                  : !devices.loading && !devices.error && <span className="text-xs text-haze-400">None detected</span>}
              </dd>
            </div>
            <div className="flex flex-col gap-1.5">
              <dt className="label-eyebrow">Printers</dt>
              <dd className="text-xs text-haze-400">
                Browsers can't enumerate printers. Server-side print jobs and their failures show up in the Print Queue.
              </dd>
            </div>
          </dl>
        </Card>
      </div>
    </CardSection>
  );
}

function SectionHeading({ icon, title }: { icon: ReactNode; title: string }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold text-accent">
      {icon}
      {title}
    </h3>
  );
}

/**
 * Lists cameras the browser can see.
 *
 * Labels are only populated once camera permission has been granted, so this is
 * best-effort: an operator configuring from a laptop sees their own webcams,
 * not the kiosk's.
 */
function useDetectedCameras() {
  const [permissionAsked] = useState(false);
  void permissionAsked;

  return useAsync(async () => {
    if (!navigator.mediaDevices?.enumerateDevices) throw new Error("Device enumeration unsupported");
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices.filter((device) => device.kind === "videoinput");
  }, []);
}
